import React, { useState } from 'react';
import { BookOpen, Search, ArrowRight, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { blogArticles } from '../data/blogArticles';
import SEOHead from '../components/SEOHead';

const BlogList = () => {
  const { i18n } = useTranslation();
  const isTe = i18n.language === 'te';
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = [
    { en: 'All', te: 'అన్నీ' },
    { en: 'Retirement & Pension', te: 'పెన్షన్ & రిటైర్‌మెంట్' },
    { en: 'Child Education', te: 'పిల్లల విద్య' },
    { en: 'Health Insurance', te: 'హెల్త్ ఇన్సూరెన్స్' },
    { en: 'NRI Insurance', te: 'ఎన్నారై బీమా' },
    { en: 'Advisory & Guidance', te: 'సలహా & మార్గదర్శకత్వం' }
  ];

  const filteredArticles = blogArticles.filter((article) => {
    const matchesSearch =
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.tags.some((t) => t.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCat = selectedCategory === 'All' || article.category === selectedCategory;
    return matchesSearch && matchesCat;
  });

  const blogSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    'name': isTe ? 'విఎస్ఆర్ ఇన్సూరెన్స్ ప్లస్ బీమా గైడ్‌లు' : 'VSR Insurance Plus Insurance Guides & Knowledge Hub',
    'description': isTe
      ? 'సీనియర్ సలహాదారు వి. ఎస్. రెడ్డి అందించే అధికారిక ఎల్ఐసి మరియు హెచ్‌డిఎఫ్‌సి ఎర్గో హెల్త్ ఇన్సూరెన్స్ సమాచార గైడ్‌లు.'
      : 'Authoritative insurance education guides written by senior advisor V. S. Reddy on LIC policies, HDFC ERGO health plans, and NRI financial planning.',
    'publisher': {
      '@type': 'InsuranceAgency',
      'name': 'VSR Insurance Plus'
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20 bg-slate-50 dark:bg-slate-950">
      <SEOHead
        title={isTe ? 'బీమా గైడ్‌లు & బ్లాగ్ | ఎల్ఐసి & హెల్త్ ఇన్సూరెన్స్ విశ్లేషణలు | వి. ఎస్. రెడ్డి' : 'Insurance Guides & Blog | LIC & Health Insights | V. S. Reddy'}
        description={isTe ? 'సీనియర్ సలహాదారు వి. ఎస్. రెడ్డి రాసిన సమగ్ర బీమా గైడ్‌లు: ఎల్ఐసి జీవన్ ఉత్సవ్, అమృతబాల్, హెచ్‌డిఎఫ్‌సి ఎర్గో ఆప్టిమా సెక్యూర్ మరియు ఎన్నారై నిబంధనలు.' : 'Explore authoritative insurance guides written by senior advisor V. S. Reddy: LIC Jeevan Utsav, Amrit Baal, HDFC ERGO Optima Secure, parents mediclaim, and NRI FEMA rules.'}
        canonicalPath="/blog"
        schemaJson={blogSchema}
        breadcrumbs={[{ name: isTe ? 'బీమా గైడ్‌లు' : 'Insurance Guides', path: '/blog' }]}
      />

      <div className="container mx-auto px-6 max-w-6xl">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 p-2.5 px-4 bg-blue-100 dark:bg-blue-900/30 text-primary rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            <BookOpen className="w-4 h-4" /> {isTe ? 'అధికారిక బీమా నాలెడ్జ్ హబ్' : 'Educational Insurance Knowledge Hub'}
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 dark:text-white mb-4 tracking-tight">
            {isTe ? 'బీమా గైడ్‌లు & పాలసీ సమీక్షలు' : 'Insurance Guides & Policy Reviews'}
          </h1>
          <p className="text-base md:text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            {isTe
              ? '25+ ఏళ్ల క్లెయిమ్స్ మరియు పాలసీ సేవల అనుభవంతో సీనియర్ సలహాదారు వి. ఎస్. రెడ్డి అందించే నిష్పాక్షిక, వాస్తవిక బీమా అవగాహన.'
              : 'Unbiased, evidence-based insurance education authored by Senior Advisor V. S. Reddy with 25+ years of practical claims and policy servicing experience.'}
          </p>
        </div>

        {/* Search & Categories */}
        <div className="max-w-2xl mx-auto mb-12 space-y-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={isTe ? 'ఆర్టికల్స్, పాలసీల పేర్లు లేదా కీవర్డ్స్‌తో శోధించండి...' : 'Search articles, policy names, or insurance topics...'}
              className="w-full pl-12 pr-4 py-3.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm focus:ring-2 focus:ring-primary focus:outline-none text-slate-900 dark:text-white"
            />
          </div>

          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((cat) => (
              <button
                key={cat.en}
                onClick={() => setSelectedCategory(cat.en)}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  selectedCategory === cat.en
                    ? 'bg-primary text-white shadow-md'
                    : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-800 hover:border-primary'
                }`}
              >
                {isTe ? cat.te : cat.en}
              </button>
            ))}
          </div>
        </div>

        {/* Articles Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredArticles.map((article) => (
            <article
              key={article.slug}
              className="bg-white dark:bg-slate-900 rounded-3xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col group"
            >
              <div className="p-6 pb-0">
                <div className="flex items-center justify-between text-xs text-slate-400 mb-3">
                  <span className="px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-primary font-semibold rounded-full">
                    {article.category}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" /> {article.readingTime}
                  </span>
                </div>

                <h2 className="text-xl font-bold text-slate-900 dark:text-white group-hover:text-primary transition-colors line-clamp-2 mb-3">
                  <Link to={`/blog/${article.slug}`}>{article.title}</Link>
                </h2>

                <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-3 leading-relaxed mb-4">
                  {article.description}
                </p>
              </div>

              <div className="mt-auto p-6 pt-4 border-t border-slate-100 dark:border-slate-800/60 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center font-bold text-xs text-primary">
                    VSR
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-slate-900 dark:text-white">{article.author}</div>
                    <div className="text-[10px] text-slate-400">{article.lastReviewed}</div>
                  </div>
                </div>

                <Link
                  to={`/blog/${article.slug}`}
                  className="inline-flex items-center gap-1 text-primary text-xs font-bold hover:translate-x-1 transition-transform"
                >
                  {isTe ? 'చదవండి' : 'Read Guide'} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </article>
          ))}
        </div>

        {filteredArticles.length === 0 && (
          <div className="text-center py-16 text-slate-500">
            <p className="text-lg font-semibold">{isTe ? 'ఎలాంటి ఆర్టికల్స్ లభించలేదు' : 'No guides match your search criteria'}</p>
            <p className="text-sm">{isTe ? 'దయచేసి వేరొక కీవర్డ్‌తో శోధించండి.' : 'Try adjusting your search terms or selecting another category.'}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogList;
